import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import Swal from 'sweetalert2'
import { viewRefundDetails, updateStatus } from '../../slice/refundSlice'
import { hasPermission } from '../../common/permissions'

const InfoRow = ({ label, value }) => (
    <div className="mb-3">
        <small className="text-muted d-block mb-1">{label}</small>
        <strong>{value ?? 'NA'}</strong>
    </div>
)

const getStatusBadge = (status) => {
    const map = {
        pending:    { bg: '#ffc107', color: '#000' },
        approved:   { bg: '#28a745', color: '#fff' },
        rejected:   { bg: '#dc3545', color: '#fff' },
        processing: { bg: '#007bff', color: '#fff' },
        completed:  { bg: '#17a2b8', color: '#fff' },
        failed:     { bg: '#343a40', color: '#fff' },
    }
    const c = map[status] || { bg: '#6c757d', color: '#fff' }
    return (
        <span style={{ backgroundColor: c.bg, color: c.color, padding: '4px 14px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', textTransform: 'capitalize' }}>
            {status || 'NA'}
        </span>
    )
}

const RefundDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const id = location.state?.id

    const { refundDetail } = useSelector(state => state.refund)
    const [actionLoading, setActionLoading] = useState(false)

    useEffect(() => {
        if (!id) { navigate('/refunds'); return }
        dispatch(viewRefundDetails({ id }))
    }, [id, dispatch, navigate])

    const rd = refundDetail
    const sb = rd?.session_booking
    const tutor = sb?.tutor
    const student = sb?.student
    const parent = rd?.parent
    const pt = rd?.parent_transaction

    const handleApprove = async () => {
        const result = await Swal.fire({
            title: 'Approve Refund?',
            text: `Approve refund of ${rd?.currency} ${parseFloat(rd?.net_refund_amount || 0).toFixed(2)} to the parent?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Approve',
            input: 'textarea',
            inputLabel: 'Admin Note (optional)',
            inputPlaceholder: 'Enter any note...',
        })
        if (!result.isConfirmed) return
        setActionLoading(true)
        const res = await dispatch(updateStatus({ id: rd.id, status: 'approved', admin_note: result.value || '' }))
        setActionLoading(false)
        if (res?.payload?.code === 200) {
            Swal.fire({ icon: 'success', title: 'Approved', timer: 1500, showConfirmButton: false })
            navigate('/refunds');
        } else {
            Swal.fire({ icon: 'error', title: 'Failed', text: res?.payload?.message || 'Something went wrong' })
        }
    }

    const handleReject = async () => {
        const result = await Swal.fire({
            title: 'Reject Refund?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, Reject',
            input: 'textarea',
            inputLabel: 'Rejection Reason *',
            inputPlaceholder: 'Enter reason for rejection...',
            inputValidator: (v) => !v && 'Rejection reason is required',
        })
        if (!result.isConfirmed) return
        setActionLoading(true)
        const res = await dispatch(updateStatus({ id: rd.id, status: 'rejected', rejection_reason: result.value }))
        setActionLoading(false)
        if (res?.payload?.code === 200) {
            Swal.fire({ icon: 'success', title: 'Rejected', timer: 1500, showConfirmButton: false })
            navigate('/refunds');
        } else {
            Swal.fire({ icon: 'error', title: 'Failed', text: res?.payload?.message || 'Something went wrong' })
        }
    }

    if (!rd || !rd.id) {
        return (
            <div className="text-center py-5">
                <i className="fe-loader" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                <h5 className="mt-3 text-muted">Loading refund details...</h5>
            </div>
        )
    }

    const isPending = rd.status === 'pending'

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="fw-semibold mb-1">Refund Details</h4>
                    <span style={{ fontFamily: 'monospace', color: '#4361ee', fontWeight: '600' }}>{rd.refund_request_no}</span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {hasPermission('refunds', 'edit') && isPending && (
                        <>
                            <button className="btn btn-success" onClick={handleApprove} disabled={actionLoading}
                                style={{ height: '38px', fontSize: '13px', borderRadius: '6px' }}>
                                <i className="fe-check me-1" /> Approve
                            </button>
                            <button className="btn btn-danger" onClick={handleReject} disabled={actionLoading}
                                style={{ height: '38px', fontSize: '13px', borderRadius: '6px' }}>
                                <i className="fe-x me-1" /> Reject
                            </button>
                        </>
                    )}
                    <button className="btn btn-outline-secondary" onClick={() => navigate('/refunds')}
                        style={{ height: '38px', fontSize: '13px', borderRadius: '6px' }}>
                        ← Back
                    </button>
                </div>
            </div>

            {/* Status Banner */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h6 className="mb-1 fw-semibold text-muted">Booking Order</h6>
                            <h5 className="mb-0 fw-bold">{sb?.order_id || 'NA'}</h5>
                            <small className="text-muted">Session Booking ID: #{rd.session_booking_id}</small>
                        </div>
                        <div className="text-center">
                            <div className="text-muted small mb-1">Status</div>
                            {getStatusBadge(rd.status)}
                        </div>
                        <div className="text-end">
                            <div className="text-muted small mb-1">Net Refund</div>
                            <h4 className="fw-bold mb-0" style={{ color: '#28a745' }}>
                                {rd.currency} {parseFloat(rd.net_refund_amount || 0).toFixed(2)}
                            </h4>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Refund Financials */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            💰 Refund Breakdown
                        </div>
                        <div className="card-body">
                            <InfoRow label="Original Amount" value={`${rd.currency} ${parseFloat(rd.original_amount || 0).toFixed(2)}`} />
                            <InfoRow label="Original VAT" value={`${rd.currency} ${parseFloat(rd.original_vat || 0).toFixed(2)}`} />
                            <InfoRow label="Admin Fee" value={`${rd.currency} ${parseFloat(rd.original_admin_fee || 0).toFixed(2)}`} />
                            <InfoRow label="Transaction Fee" value={`${rd.currency} ${parseFloat(rd.original_transaction_fee || 0).toFixed(2)}`} />
                            <hr />
                            <InfoRow label="Requested Amount" value={`${rd.currency} ${parseFloat(rd.requested_amount || 0).toFixed(2)}`} />
                            <InfoRow label="Telr Refund Fee" value={`${rd.currency} ${parseFloat(rd.telr_refund_fee || 0).toFixed(2)}`} />
                            <InfoRow label="Admin Deduction Fee" value={`${rd.currency} ${parseFloat(rd.admin_deduction_fee || 0).toFixed(2)}`} />
                            <div className="mb-0 p-3 rounded" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                <small className="text-muted d-block mb-1">Net Refund Amount</small>
                                <strong style={{ fontSize: '18px', color: '#15803d' }}>{rd.currency} {parseFloat(rd.net_refund_amount || 0).toFixed(2)}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Refund Request Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            📋 Request Info
                        </div>
                        <div className="card-body">
                            <InfoRow label="Reason" value={rd.reason} />
                            <InfoRow label="Reason Note" value={rd.reason_note} />
                            <InfoRow label="Approved Amount" value={rd.approved_amount ? `${rd.currency} ${parseFloat(rd.approved_amount).toFixed(2)}` : null} />
                            <InfoRow label="Admin Note" value={rd.admin_note} />
                            <InfoRow label="Rejection Reason" value={rd.rejection_reason} />
                            <InfoRow label="Reviewed By" value={rd.reviewer ? `${rd.reviewer.first_name || ''} ${rd.reviewer.last_name || ''}`.trim() : null} />
                            <InfoRow label="Reviewed At" value={rd.reviewed_at ? moment(rd.reviewed_at).local().format('DD MMM YYYY, hh:mm A') : null} />
                            <InfoRow label="Environment" value={<span className="badge bg-secondary">{rd.environment}</span>} />
                        </div>
                    </div>
                </div>

                {/* Parent Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            👤 Parent Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={parent?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="Parent"
                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png' }}
                                    style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #f8f9fa' }}
                                />
                                <div>
                                    <h6 className="mb-0 fw-semibold">{parent?.first_name} {parent?.last_name}</h6>
                                    <small className="text-muted">ID: {parent?.id}</small>
                                </div>
                            </div>
                            <InfoRow label="Email" value={parent?.email} />
                            <InfoRow label="Phone" value={parent?.country_code && parent?.phone_number ? `${parent.country_code} ${parent.phone_number}` : null} />
                        </div>
                    </div>
                </div>

                {/* Tutor Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            🎓 Tutor Information
                        </div>
                        <div className="card-body">
                            <div className="d-flex align-items-center gap-3 mb-3">
                                <img
                                    src={tutor?.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                    alt="Tutor"
                                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png' }}
                                    style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #f8f9fa' }}
                                />
                                <div>
                                    <h6 className="mb-0 fw-semibold">{tutor?.first_name} {tutor?.last_name}</h6>
                                    <small className="text-muted">ID: {tutor?.id}</small>
                                    {tutor?.profile_status && (
                                        <div><span className="badge bg-success mt-1" style={{ fontSize: '10px' }}>{tutor.profile_status}</span></div>
                                    )}
                                </div>
                            </div>
                            <InfoRow label="Email" value={tutor?.email} />
                            <InfoRow label="Phone" value={tutor?.country_code && tutor?.phone_number ? `${tutor.country_code} ${tutor.phone_number}` : null} />
                        </div>
                    </div>
                </div>

                {/* Session Booking Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            📅 Session Booking
                        </div>
                        <div className="card-body">
                            <InfoRow label="Order ID" value={sb?.order_id} />
                            <InfoRow label="Session Type" value={sb?.session_type} />
                            <InfoRow label="Total Sessions" value={sb?.total_sessions} />
                            <InfoRow label="Sessions Used" value={sb?.sessions_used} />
                            <InfoRow label="Sessions Remaining" value={sb?.sessions_remaining} />
                            <InfoRow label="Price / Session" value={sb?.price_per_session ? `${rd.currency} ${parseFloat(sb.price_per_session).toFixed(2)}` : null} />
                            <InfoRow label="Total Amount" value={sb?.total_amount ? `${rd.currency} ${parseFloat(sb.total_amount).toFixed(2)}` : null} />
                            <InfoRow label="Commission Rate" value={sb?.commission_rate ? `${sb.commission_rate}%` : null} />
                            <InfoRow label="Booking Status" value={getStatusBadge(sb?.status)} />
                            <InfoRow label="Payment Status" value={sb?.payment_status} />
                            <InfoRow label="Payment Method" value={sb?.payment_method} />
                            <InfoRow label="Start Date" value={sb?.start_date} />
                            <InfoRow label="Purchased At" value={sb?.purchased_at ? moment(sb.purchased_at).local().format('DD MMM YYYY, hh:mm A') : null} />
                            <InfoRow label="Expires At" value={sb?.expires_at ? moment(sb.expires_at).local().format('DD MMM YYYY') : null} />
                        </div>
                    </div>
                </div>

                {/* Student Info */}
                {student && (
                    <div className="col-md-6 mb-4">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                                🧒 Student Information
                            </div>
                            <div className="card-body">
                                <InfoRow label="Full Name" value={student.full_name} />
                                <InfoRow label="Age" value={student.age} />
                                <InfoRow label="Year / Grade" value={student.year_group_grade} />
                                <InfoRow label="Preferred Language" value={student.preferred_lang} />
                                <InfoRow label="Learning Goal" value={student.learning_goal} />
                                <InfoRow label="City" value={student.city} />
                                <InfoRow label="State" value={student.state} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Transaction Info */}
                {pt && (
                    <div className="col-md-6 mb-4">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                            <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                                💳 Transaction Details
                            </div>
                            <div className="card-body">
                                <InfoRow label="Cart ID" value={pt.cart_id} />
                                <InfoRow label="Tran Ref" value={pt.tran_ref} />
                                <InfoRow label="Order Ref" value={<span style={{ fontSize: '11px', wordBreak: 'break-all' }}>{pt.order_ref}</span>} />
                                <InfoRow label="Transaction Status" value={pt.tran_status} />
                                <InfoRow label="Payment Status" value={pt.payment_status} />
                                <InfoRow label="Payment Method" value={pt.payment_method} />
                                <InfoRow label="Amount" value={`${pt.currency} ${parseFloat(pt.amount || 0).toFixed(2)}`} />
                                <InfoRow label="Admin Fee" value={`${pt.currency} ${parseFloat(pt.admin_fee || 0).toFixed(2)}`} />
                                <InfoRow label="Net Amount" value={`${pt.currency} ${parseFloat(pt.net_amount || 0).toFixed(2)}`} />
                                <InfoRow label="VAT" value={`${pt.currency} ${parseFloat(pt.vat || 0).toFixed(2)}`} />
                                <InfoRow label="Commission %" value={pt.commision_percentage ? `${pt.commision_percentage}%` : null} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Telr / Gateway Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            🔗 Telr Gateway Info
                        </div>
                        <div className="card-body">
                            <InfoRow label="Telr Refund Status" value={rd.telr_refund_status} />
                            <InfoRow label="Telr Refund Ref" value={rd.telr_refund_ref} />
                            <InfoRow label="Telr Trace" value={rd.telr_refund_trace} />
                            <InfoRow label="Telr % Fee" value={rd.telr_refund_percentage} />
                            <InfoRow label="Telr Fixed Fee" value={rd.telr_refund_fixed} />
                            <InfoRow label="Initiated At" value={rd.telr_initiated_at ? moment(rd.telr_initiated_at).local().format('DD MMM YYYY, hh:mm A') : null} />
                            <InfoRow label="Confirmed At" value={rd.telr_confirmed_at ? moment(rd.telr_confirmed_at).local().format('DD MMM YYYY, hh:mm A') : null} />
                            <InfoRow label="Error Message" value={rd.error_message} />
                            <InfoRow label="Error Note" value={rd.error_note} />
                        </div>
                    </div>
                </div>

                {/* Record Info */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                            🕒 Record Information
                        </div>
                        <div className="card-body">
                            <InfoRow label="Created At" value={rd.createdAt ? moment(rd.createdAt).local().format('DD MMM YYYY, hh:mm A') : null} />
                            <InfoRow label="Updated At" value={rd.updatedAt ? moment(rd.updatedAt).local().format('DD MMM YYYY, hh:mm A') : null} />
                            <InfoRow label="Active" value={rd.is_active === 1 ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RefundDetail